import { Component, ElementRef, Input, signal, viewChild } from '@angular/core';
import { BaseFieldComponent } from '../base-field/base-field.component';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BaseChangeEvent, FieldBase } from '@nagel-types';
import { CUSTOM_ERROR_MESSAGES, CUSTOM_FORM_ERRORS } from '../../formErrorsConsts';
import { ErrorStateMatcher } from '@angular/material/core';
import { getCommonErrorMessage } from '@nagel-utils';
import { DynamicFollowSuffix } from '@nagel-directives';

@Component({
    selector: 'lib-input-field',
    providers: [],
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        DynamicFollowSuffix
    ],
    templateUrl: './input-field.component.html',
    styleUrl: './input-field.component.scss',
})
export class InputFieldComponent extends BaseFieldComponent {
    @Input() override field!: FieldBase<unknown>;
    @Input() override form!: FormGroup;
    private inputElement =  viewChild.required<ElementRef<HTMLInputElement>>('innerInput');

    control: FormControl | null = null;

    @Input() key: string = '';
    @Input() errorStateMatcher: ErrorStateMatcher = new ErrorStateMatcher();

    @Input() value: string = '';
    @Input() className: string = '';
    @Input() icon: string = '';
    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input() type: string = '';
    @Input() readonly: boolean = false;
    @Input() disabled: boolean = false;
    @Input() suffix: string = '';
    @Input() hint: string = '';
    @Input() onBlur: ((event: FocusEvent) => void) | undefined = undefined;
    @Input() onChange: ((event: BaseChangeEvent) => void) | undefined = undefined;
    @Input() onFinishEditKeyPress: ((event: KeyboardEvent) => void) | undefined = undefined;

    showSuffix = signal<boolean>(false);
    valueLength = 0;
    suffixOffset = '0px';

    ngOnInit() {
        if (this.value) {
            this.showSuffix.set(true);
            this.valueLength = (this.value as string).length;
            this.updateSuffixOffset(this.valueLength);
        }

        if (this.field) {
            this.key = this.field.key;
            this.className = this.field.className;
            this.icon = this.field.icon;
            this.label = this.field.label;
            this.placeholder = this.field.placeholder;
            this.type = this.field.type;
            this.readonly = this.field.readonly;
            this.disabled = this.field.disabled ?? this.disabled;
            this.suffix = this.field.suffix ?? this.suffix;
            this.hint = this.field.hint ?? this.hint;
            this.onChange = this.field.onChange ?? this.onChange;
            this.onBlur = this.field.onBlur ?? this.onBlur;
        }
    }

    ngOnChanges() {
        this.control = this.form?.get(this.key) as FormControl;
        this.showSuffix.set(!!this.control?.value);
    }

    handleOnBlur(event: FocusEvent) {
        if (!this.onBlur) {
            return;
        }

        const hasFormError = this.form && this.getFormHasError();

        if (!this.form || !hasFormError) {
            this.onBlur(event);
        }
    };

    getFormHasError = () => {
        const fieldControl = this.form.get(this.key);

        return !!fieldControl?.errors && Object.keys(fieldControl.errors || {}).length;
    };

    override isApplicable(fieldType: string) {
        return fieldType === 'input';
    }

    handleOnInput(event: Event) {
        const value = (event.target as HTMLInputElement).value || '';
        this.showSuffix.set(!!value);
        this.valueLength = value.length;
        this.updateSuffixOffset(this.valueLength);

        if (this.onChange) {
            this.onChange({ value, key: this.key });
        }
    }

    private updateSuffixOffset(length: number) {
        const charWidth = 8.4;
        const offset = length * charWidth;
        this.suffixOffset = `${offset}px`;
    }

    getCustomError(control: AbstractControl | null): string | null {
        if (!control?.errors) { return null; }

        const customErrorKeys = Object.values(CUSTOM_FORM_ERRORS);

        const key = Object.keys(control.errors).find((errorKeys) =>
            customErrorKeys.includes(errorKeys)
        );

        return key ? control.errors[key] : null;
    }

    getValidationErrors(control: AbstractControl | null): string | null {
        if (!control?.errors) { return null; }

        const validationErrorKeys = Object.keys(CUSTOM_ERROR_MESSAGES);
        let validationMessage: string | null = null;

        const key = Object.keys(control.errors).find((errorKey) =>
            validationErrorKeys.includes(errorKey)
        );

        if (key) {
            const errorFormatter = CUSTOM_ERROR_MESSAGES[key as keyof typeof CUSTOM_ERROR_MESSAGES];
            const value = control.errors[key as keyof typeof control.errors][key as keyof typeof control.errors];

            validationMessage = errorFormatter ? `${errorFormatter(value, this.suffix)}` : null;
        } else {
            const firstErrorKey = Object.keys(control.errors)[0];
            const firstError = control.errors[firstErrorKey as keyof typeof control.errors];
            const { message } = firstError;

            if (message) {
                validationMessage = message;
            } else {
                const commonErrorMessage = getCommonErrorMessage(control.errors, this.label);

                if (commonErrorMessage) {
                    validationMessage = commonErrorMessage;
                }
            }
        }

        return validationMessage;
    }

    focus = () => {
        this.inputElement().nativeElement.focus();
    };

    handleEnterPress = (event: KeyboardEvent) => {
        if (!this.onFinishEditKeyPress) {
            return;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
            this.onFinishEditKeyPress(event);
        }
    };
}
